import type {ReactElement, ReactNode} from 'react';
import {useEffect, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {GoogleLoginButton} from '@Components/google-login-button';
import {ButtonFacebookLogin} from '@Components/button-facebook-login';
import {LineDivider} from '@Components/line-divider';
import {
  areThirdPartyButtonsNeededForType,
  AUTH_TYPE,
  isInactivePasswordResetAuthType, isLoginAuthType,
  isLoginOrSignupAuthType,
  isLoginPageShortViewport,
  isLoginPageWidescreen,
  isOAuthFlow,
  isPhoneScreen,
  isSignupAuthType,
  LOGIN_OPTIONS,
  onFacebookConnect,
  SavedLoginMethod,
} from '@Libraries/login-signup-library';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {useLoginPageAuthType, useLoginPageSavedAccounts, useLoginPageSelectedSavedAccountHashedId, useLoginPageTwoFactorVerificationOrigin} from '@Components/login-page/login-page.hooks';
import {SignupForm} from '@Components/signup-form';
import {selectSavedAccount} from '@Components/login-page/login-page-reducer';
import {useAppDispatch} from '@/hooks';
import {useGoogleSignIn} from '@Hooks/login-signup/useGoogleSignIn';
import {useAppleSignIn} from '@Hooks/login-signup/useAppleSignIn';
import {useFacebookSignIn} from '@Hooks/login-signup/useFacebookSignIn';
import {assertNever} from '@Utils/assert-never.util';
import type {SavedLoginAccount} from '@Libraries/login-signup-library';
import {SavedAccountsPanel} from '@Components/login-page/components/saved-accounts-panel/saved-accounts-panel';
import {SavedAccountsLayout} from '@Components/login-page/components/saved-accounts-panel/saved-accounts-panel.types';
import {StudentLoginForm} from '@Components/student-login-form';
import {TwoFactorVerificationForm} from '@Components/two-factor-verification-form/two-factor-verification-form';
import {useTwoFactorAuthenticationPanelLoginType} from '@Components/two-factor-authentication/two-factor-authentication.hooks';
import {clearError as clearTwoFactorError, updateLoginType as updateTwoFactorLoginType} from '@Components/two-factor-authentication/two-factor-authentication-reducer';
import {LoginType as TwoFactorLoginType} from '@Components/two-factor-authentication/two-factor-authentication.types';
import {isTOTPLoginType} from '@Libraries/two-factor-authentication-library';
import {ConfirmLoginForm} from '@Components/confirm-login-form';
import {ForgotPasswordForm} from '@Components/forgot-password-form';
import {LoginForm} from '@Components/login-form';
import {AuthFormFooterCta} from '@Components/auth-form-footer-cta';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {SocialMediaIconsContainer} from '@Components/social-media-icons-container';
import {AppleLogin} from '@Components/apple-login';
import {AuthSsoButton} from '@Components/auth-sso-button/auth-sso-button';
import {useUpdateAuthFormTypes} from '@/hooks/login-signup/useUpdateAuthFormTypes';
import useWindowSize from '@/hooks/useWindowSize';
import styles from './login-page-content.module.scss';
import {useUpdateErrorStates} from '@/hooks/login-signup/useUpdateErrorStates';
import {SignupAgreement} from '@Components/signup-form/components/signup-agreement';
import {InactiveUserPasswordResetForm} from '@Components/inactive-user-password-reset-form';
import {AuthTestId} from '@/test-ids/auth-test-id';
import type {FacebookConnectCallback, GoogleSignInCallback} from '@Components/login-page/login-page.types';
import type {LoginPageContentProps} from './login-page-content.types';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal/modal';
import {AuthSurface, SMALL_SCREEN_HEIGHT_BREAKPOINT} from './login-page-content.types';

export function LoginPageContent({
  onAuthTypeChangeCallback,
  onPanelChange,
  loginOptionsMode,
  isUserJoiningTeam,
  teamName = '',
  redirectUrl = '',
  surface = AuthSurface.PAGE,
  heading = '',
  onAuthSuccess,
  onAuthError,
  onSheetHeadingChange,
}: LoginPageContentProps): ReactElement<any> {
  const [showForm, setShowForm] = useState(false);
  const windowScreenSize = useWindowSize();

  const type = useLoginPageAuthType();
  const twoFactorVerificationOrigin = useLoginPageTwoFactorVerificationOrigin();
  const isWidescreen = surface === AuthSurface.PAGE && isLoginPageWidescreen(windowScreenSize.windowWidth);
  const isPhoneLayout = isPhoneScreen(windowScreenSize.windowWidth);
  const isCompactSsoLayout = !isWidescreen && (isPhoneLayout || isLoginPageShortViewport(windowScreenSize.windowHeight));
  /** The modal below the Modal component's own phone threshold, where it renders as a bottom sheet. */
  const isModalSheetLayout = surface === AuthSurface.MODAL && windowScreenSize.windowWidth < MOBILE_MODAL_WIDTH_THRESHOLD;
  /**
   * Signup pairs its providers at phone width on the login page. The modal pairs them wherever
   * log-in does — a short viewport (D46) or phone width, the sheet included.
   */
  const isCompactSignupSsoLayout = surface === AuthSurface.MODAL ? isCompactSsoLayout : isPhoneLayout;
  const twoFactorLoginType = useTwoFactorAuthenticationPanelLoginType();
  const {updateAuthTypesAndClearErrors} = useUpdateAuthFormTypes();
  const {hideErrors} = useUpdateErrorStates();
  const dispatch = useAppDispatch();
  const savedAccounts = useLoginPageSavedAccounts();
  const selectedSavedAccountHashedId = useLoginPageSelectedSavedAccountHashedId();

  /**
   * The login page navigates away on success, so it passes no handler and each button keeps its own
   * redirect. A surface that stays open supplies `onAuthSuccess` and gets these adapters, which are also
   * where a provider answering without a usable account becomes an error.
   *
   * The decision itself is synchronous; Google and Facebook type their callbacks as promise-returning and
   * Apple does not, so each adapter only reshapes the return to match its own button.
   */
  const onSsoSignInDone = (userId: string): void => {
    if (Number.isNaN(Number.parseInt(userId, 10))) {
      onAuthError?.();
      return;
    }
    onAuthSuccess?.();
  };

  const getAppleSignInHandler = (): ((userId: string) => void) | undefined => {
    return onAuthSuccess === undefined ? undefined : onSsoSignInDone;
  };

  const getGoogleSignInHandler = (): GoogleSignInCallback | undefined => {
    return onAuthSuccess === undefined ? undefined : onGoogleSignInDone;
  };

  const onGoogleSignInDone = (userId: string): Promise<void> => {
    onSsoSignInDone(userId);
    return Promise.resolve();
  };

  const getFacebookConnectHandler = (): FacebookConnectCallback => {
    return onAuthSuccess === undefined ? onFacebookConnect.bind(null, redirectUrl) : onFacebookConnectDone;
  };

  const onFacebookConnectDone = (isConnected: boolean): Promise<void> => {
    if (isConnected) {
      onAuthSuccess?.();
    } else {
      onAuthError?.();
    }
    return Promise.resolve();
  };

  const getGoogleRedirectUrl = (): string => {
    return isUserJoiningTeam ? redirectUrl : '';
  };

  const {signInWithGoogle} = useGoogleSignIn({redirectUrl: getGoogleRedirectUrl(), onSignInDone: getGoogleSignInHandler()});
  const {signInWithApple} = useAppleSignIn({redirectUrl, onSignInDone: getAppleSignInHandler()});
  const {signInWithFacebook} = useFacebookSignIn({onConnect: getFacebookConnectHandler()});

  const updateStatesOnFormTypeChange = (formType: AUTH_TYPE): void => {
    updateAuthTypesAndClearErrors(formType, undefined, onAuthTypeChangeCallback);
  };

  const changeToSignupPage = (): void => {
    updateStatesOnFormTypeChange(AUTH_TYPE.SIGNUP);
  };

  const changeToLoginPage = (): void => {
    updateStatesOnFormTypeChange(AUTH_TYPE.LOGIN);
  };

  /**
   * `Continue` on a remembered account (§32) never holds a credential, so it re-authenticates by the account's
   * method: the password step for an email account, the provider's consent window for the rest.
   */
  const continueWithSavedAccount = (account: SavedLoginAccount): void => {
    dispatch(selectSavedAccount(account.hashedId));
    switch (account.method) {
      case SavedLoginMethod.WEB:
        updateStatesOnFormTypeChange(AUTH_TYPE.SAVED_ACCOUNT_PASSWORD);
        break;
      case SavedLoginMethod.GOOGLE:
        void signInWithGoogle();
        break;
      case SavedLoginMethod.APPLE:
        void signInWithApple();
        break;
      case SavedLoginMethod.FACEBOOK:
        void signInWithFacebook();
        break;
      default:
        assertNever(account.method);
    }
  };

  const changeToStudentLoginPage = (): void => {
    updateStatesOnFormTypeChange(AUTH_TYPE.STUDENT_LOGIN);
  };

  /** The phone sheet's header row holds about 260px beside its two controls, so it gets the short form of the TOTP heading. */
  const getTwoFactorHeading = (): string => {
    if (!isTOTPLoginType(twoFactorLoginType)) {
      return window.i18next.t('pmwjs_two_factor_authentication_verification_backup_code_heading');
    }
    return isModalSheetLayout
      ? window.i18next.t('pmwjs_two_factor_authentication_verification_totp_heading')
      : window.i18next.t('pmwjs_two_factor_authentication_login_heading');
  };

  const changeToSignupPasswordStep = (): void => {
    updateStatesOnFormTypeChange(AUTH_TYPE.SIGNUP_PASSWORD);
  };

  /**
   * Withholding the handler keeps `SignupForm` single-step. The OAuth panels ask for the password on the
   * same screen; there is no second panel in that flow to hand off to.
   */
  const getEmailStepCompleteHandler = (): (() => void) | undefined => {
    return isOAuthFlow(loginOptionsMode) ? undefined : changeToSignupPasswordStep;
  };

  const changeToForgotPasswordPage = (): void => {
    updateStatesOnFormTypeChange(AUTH_TYPE.FORGOT_PASSWORD);
  };

  const changeToInactivePasswordResetPage = (): void => {
    updateStatesOnFormTypeChange(AUTH_TYPE.INACTIVE_PASSWORD_RESET);
  };

  const getFormHeading = (): string => {
    if (heading !== '' && isLoginOrSignupAuthType(type)) {
      return heading;
    }

    switch (type) {
      case AUTH_TYPE.SIGNUP:
        return getSignupFormHeading();
      case AUTH_TYPE.SIGNUP_PASSWORD:
        return window.i18next.t('pmwjs_set_your_password');
      case AUTH_TYPE.STUDENT_LOGIN:
        return window.i18next.t('pmwjs_student_login_heading');
      case AUTH_TYPE.CONFIRM_LOGIN:
        return window.i18next.t('pmwjs_just_one_more_step');
      case AUTH_TYPE.FORGOT_PASSWORD:
        return window.i18next.t('pmwjs_forgot_password_heading');
      case AUTH_TYPE.TWO_FACTOR_VERIFICATION:
        return getTwoFactorHeading();
      case AUTH_TYPE.SAVED_ACCOUNT_PASSWORD:
        return window.i18next.t('pmwjs_log_in_to_your_account');
      case AUTH_TYPE.LOGIN:
        return getLoginFormHeading();
      case AUTH_TYPE.INACTIVE_PASSWORD_RESET:
        return '';
      default:
        return window.i18next.t('pmwjs_user_log_in');
    }
  };

  /** An invitee arrives here mid-join, so the heading names the team rather than the product. */
  const getSignupFormHeading = (): string => {
    if (isUserJoiningTeam && teamName !== '') {
      return window.i18next.t('pmwjs_create_account_to_join_team', {teamName});
    }
    return window.i18next.t('pmwjs_user_sign_up_heading');
  };

  const getLoginFormHeading = (): string => {
    if (isOAuthFlow(loginOptionsMode)) {
      return window.i18next.t('pmwjs_user_log_in');
    }
    // An invitee arrives here mid-join, so the heading names the team rather than the product.
    if (isUserJoiningTeam && teamName !== '') {
      return window.i18next.t('pmwjs_log_in_to_join_team', {teamName});
    }
    return showForm ? window.i18next.t('pmwjs_separated_login_with_email') : window.i18next.t('pmwjs_user_log_in');
  };

  const onFormVisibilityChange = (): void => {
    onPanelChange();
    hideErrors();
  };

  /** The backup-code view steps back to the authenticator code first; only the code view leaves the panel. */
  const onTwoFactorBackButton = (): void => {
    if (isTOTPLoginType(twoFactorLoginType)) {
      updateStatesOnFormTypeChange(twoFactorVerificationOrigin);
      return;
    }
    dispatch(updateTwoFactorLoginType(TwoFactorLoginType.TOTP));
    dispatch(clearTwoFactorError());
  };

  const onBackButton = (): void => {
    switch (type) {
      case AUTH_TYPE.TWO_FACTOR_VERIFICATION:
        onTwoFactorBackButton();
        break;
      case AUTH_TYPE.SIGNUP_PASSWORD:
        updateStatesOnFormTypeChange(AUTH_TYPE.SIGNUP);
        break;
      case AUTH_TYPE.SAVED_ACCOUNT_PASSWORD:
        updateStatesOnFormTypeChange(AUTH_TYPE.SAVED_ACCOUNTS);
        break;
      case AUTH_TYPE.LOGIN:
      case AUTH_TYPE.SIGNUP:
        setShowForm(false);
        onFormVisibilityChange();
        break;
      default:
        changeToLoginPage();
    }
  };

  useEffect(() => {
    if (!isModalSheetLayout || onSheetHeadingChange === undefined) {
      return;
    }
    onSheetHeadingChange({title: getFormHeading(), hasBackControl: hasPanelHeadingWithBackControl(), onBack: onBackButton});
  }, [type, showForm, heading, twoFactorLoginType, isModalSheetLayout]);

  const onShowFormButtonClick = (): void => {
    setShowForm(true);
    onFormVisibilityChange();
  };

  const getShowFormButton = (): ReactNode => {
    return (
      <Button
        data-testid={AuthTestId.SHOW_FORM_BUTTON}
        text={window.i18next.t('pmwjs_user_continue_with_email')}
        type={ButtonType.PRIMARY}
        customClasses="-fullwidth spacing-m-t-3"
        icon="icon-envelope"
        size={ButtonSize.MEDIUM}
        onClick={onShowFormButtonClick}
      />
    );
  };

  const isSmallScreenLayout = (): boolean => {
    return isLoginOrSignupAuthType(type) && windowScreenSize.windowHeight < SMALL_SCREEN_HEIGHT_BREAKPOINT;
  };

  const shouldFormBeHidden = (): boolean => {
    if (isStandardLoginPanel()) {
      return false;
    }
    if (isSignupAuthType(type) || type === AUTH_TYPE.SIGNUP_PASSWORD) {
      return false;
    }
    return isSmallScreenLayout() && !showForm && !isOAuthFlow(loginOptionsMode) && !isInactivePasswordResetAuthType(type);
  };

  const getLegacyOrDivider = (): ReactNode => {
    if (isStandardLoginPanel() || isSmallScreenLayout() || isSignupAuthType(type)) {
      return null;
    }
    return <LineDivider className={styles.dividerContainer} dividerClassName={styles.orLineDivider} includeTextInDivider />;
  };

  /**
   * On the login panel the "Or" rule and the form are one block: the design spaces them 20px apart and
   * sets them 32px below the provider buttons. The rule is unconditional here — the legacy one hides
   * itself below 1000px of viewport height, which would hide it on ordinary 720px-tall screens.
   *
   * The form needs its own wrapper because LoginForm returns a fragment: without one, its fields become
   * flex children of this section and each inherits the 20px gap meant for the rule.
   *
   * The OAuth panels have no providers — connecting an app requires a PosterMyWall password — so there is
   * nothing for the rule to divide and it goes with them.
   */
  const getFormSection = (): ReactNode => {
    if (isSavedAccountsPanel()) {
      return (
        <SavedAccountsPanel
          layout={isModalSheetLayout ? SavedAccountsLayout.SHEET : SavedAccountsLayout.STANDARD}
          onAllAccountsRemoved={changeToLoginPage}
          onContinue={continueWithSavedAccount}
          onUseAnotherAccount={changeToLoginPage}
        />
      );
    }
    if (isSignupPasswordPanel() || isSavedAccountPasswordPanel()) {
      return getFormBody();
    }
    if (!isStandardLoginPanel() && !isStandardSignupPanel()) {
      return getForm();
    }
    if (isOAuthFlow(loginOptionsMode)) {
      return getFormBody();
    }
    return (
      <div className={styles.loginPanelFormSection}>
        <LineDivider className={styles.orDivider} dividerClassName={styles.orDividerLine} includeTextInDivider />
        {getFormBody()}
      </div>
    );
  };

  /**
   * The signup terms bind to the form rather than to the panel, and they sit 24px apart —
   * tighter than the column's own rhythm — so they share a wrapper instead of sitting in the column.
   */
  const getFormBody = (): ReactElement => {
    const signupAgreement = getSignupAgreement();
    if (signupAgreement === null) {
      return <div className={styles.loginPanelForm}>{getForm()}</div>;
    }
    return (
      <div className={styles.formWithSignupAgreement}>
        <div className={styles.loginPanelForm}>{getForm()}</div>
        {signupAgreement}
      </div>
    );
  };

  const getSignupAgreement = (): ReactElement | null => {
    if (!isStandardSignupPanel() && !isSignupPasswordPanel()) {
      return null;
    }
    return (
      <div className={styles.signupAgreementSlot}>
        <SignupAgreement isTextCenterAligned textSize={TextSize.MD} />
      </div>
    );
  };

  /** The account the chooser recorded before opening the password step (§32). */
  const getSelectedSavedAccountEmail = (): string => {
    const selectedAccount = savedAccounts.find((account) => {
      return account.hashedId === selectedSavedAccountHashedId;
    });
    return selectedAccount?.email ?? '';
  };

  const getSavedAccountPasswordForm = (): ReactElement => {
    return (
      <LoginForm
        lockedEmail={getSelectedSavedAccountEmail()}
        loginOptionsMode={loginOptionsMode}
        showSmallButton={!isWidescreen}
        switchToForgotPassword={changeToForgotPasswordPage}
        switchToInactivePasswordReset={changeToInactivePasswordResetPage}
        onAuthFormSubmitSuccess={onAuthSuccess}
      />
    );
  };

  const getForm = (): ReactNode => {
    if (shouldFormBeHidden()) {
      return getShowFormButton();
    }

    switch (type) {
      case AUTH_TYPE.SIGNUP:
      case AUTH_TYPE.SIGNUP_PASSWORD:
        return <SignupForm onEmailStepComplete={getEmailStepCompleteHandler()} showSmallButton={!isWidescreen} onAuthFormSubmitSuccess={onAuthSuccess} />;
      case AUTH_TYPE.STUDENT_LOGIN:
        return <StudentLoginForm loginRedirect={changeToLoginPage} showSmallButton={!isWidescreen} onAuthFormSubmitSuccess={onAuthSuccess} />;
      case AUTH_TYPE.CONFIRM_LOGIN:
        return <ConfirmLoginForm showSmallButton={!isWidescreen} onAuthFormSubmitSuccess={onAuthSuccess} />;
      case AUTH_TYPE.TWO_FACTOR_VERIFICATION:
        return <TwoFactorVerificationForm />;
      case AUTH_TYPE.FORGOT_PASSWORD:
        return <ForgotPasswordForm showSmallButton={!isWidescreen} />;
      case AUTH_TYPE.INACTIVE_PASSWORD_RESET:
        return <InactiveUserPasswordResetForm />;
      case AUTH_TYPE.SAVED_ACCOUNT_PASSWORD:
        return getSavedAccountPasswordForm();
      case AUTH_TYPE.LOGIN:
      default:
        return (
          <LoginForm
            loginOptionsMode={loginOptionsMode}
            showSmallButton={isStandardLoginPanel() && !isWidescreen}
            switchToForgotPassword={changeToForgotPasswordPage}
            switchToInactivePasswordReset={changeToInactivePasswordResetPage}
            onAuthFormSubmitSuccess={onAuthSuccess}
          />
        );
    }
  };

  const getActionItems = (): ReactNode => {
    switch (type) {
      // Both signup panels carry their prompt inline under the heading instead.
      case AUTH_TYPE.SIGNUP_PASSWORD:
      case AUTH_TYPE.SIGNUP:
        return null;
      case AUTH_TYPE.LOGIN:
      default:
        return (
          <div className={styles.ctaButtons}>
            <AuthFormFooterCta text={window.i18next.t('pmwjs_user_sign_up_prompt')} btnText={window.i18next.t('pmwjs_user_sign_up')} onClick={changeToSignupPage} btnTestId={AuthTestId.SIGN_UP_BUTTON} />
          </div>
        );
    }
  };

  /**
   * Both panels pair their providers up once space is tight, but they do not pair the same way: log-in's
   * four split evenly, while signup's three put Google on its own row
   * and pair the other two below it. Everywhere else they stay in one column.
   */
  const getSsoButtonStackClassName = (): string => {
    if (!shouldThirdPartyButtonsBeShown()) {
      return '_hidden';
    }
    if (!areSsoButtonsPaired()) {
      return styles.ssoButtonStack;
    }
    return isStandardSignupPanel()
      ? `${styles.ssoButtonStack} ${styles.ssoButtonGrid} ${styles.ssoButtonGridWithFullWidthLead}`
      : `${styles.ssoButtonStack} ${styles.ssoButtonGrid}`;
  };

  const getSsoButtonSize = (): ButtonSize => {
    return isWidescreen ? ButtonSize.LARGE : ButtonSize.MEDIUM;
  };

  /** Widescreen sets the account prompt one size up, 14 → 16, but leaves the heading unchanged. */
  const getAccountPromptTextSize = (): TextSize => {
    return isWidescreen ? TextSize.LG : TextSize.MD;
  };

  /**
   * The short provider names are drawn only where the buttons pair up (the 2×2 log-in grid, signup's Google-led
   * pairs). A full-width stack keeps the full call to action even when the screen is short.
   */
  const areSsoButtonsPaired = (): boolean => {
    if (isStandardLoginPanel()) {
      return isCompactSsoLayout;
    }
    if (isStandardSignupPanel()) {
      return isCompactSignupSsoLayout;
    }
    return false;
  };

  const getStudentLoginButtonText = (): string => {
    return areSsoButtonsPaired() ? window.i18next.t('pmwjs_student') : window.i18next.t('pmwjs_log_in_as_student');
  };

  const getStudentLoginButton = (): ReactNode => {
    if (isSignupAuthType(type) || type === AUTH_TYPE.SIGNUP_PASSWORD || loginOptionsMode !== LOGIN_OPTIONS.DEFAULT) {
      return null;
    }
    return (
      <AuthSsoButton
        data-testid={AuthTestId.USE_STUDENT_LOGIN_BUTTON}
        icon="icon-graduation"
        onClick={changeToStudentLoginPage}
        size={getSsoButtonSize()}
        text={getStudentLoginButtonText()}
      />
    );
  };

  const shouldThirdPartyButtonsBeShown = (): boolean => {
    return areThirdPartyButtonsNeededForType(type) && loginOptionsMode !== LOGIN_OPTIONS.OAUTH && !showForm;
  };

  /**
   * The standalone log-in panel, as opposed to the signup / student / confirm-login / forgot-password
   * / inactive-password-reset panels this component also renders, and as opposed to the OAuth consent
   * variant, which carries its own message and spacing.
   */
  const isStandardLoginPanel = (): boolean => {
    return type === AUTH_TYPE.LOGIN;
  };

  const isStandardSignupPanel = (): boolean => {
    return type === AUTH_TYPE.SIGNUP;
  };

  /** The remembered-accounts panel (§32). It owns its heading and shows no providers. */
  const isSavedAccountsPanel = (): boolean => {
    return type === AUTH_TYPE.SAVED_ACCOUNTS;
  };

  /**
   * The second signup step. It drops the provider buttons and leads with a back
   * control, so it takes the tighter column the other back-control panels use.
   */
  const isSignupPasswordPanel = (): boolean => {
    return type === AUTH_TYPE.SIGNUP_PASSWORD;
  };

  /**
   * The password step for a remembered email account (§32): a back control over the
   * log-in form with its email locked, and no providers.
   */
  const isSavedAccountPasswordPanel = (): boolean => {
    return type === AUTH_TYPE.SAVED_ACCOUNT_PASSWORD;
  };

  /**
   * The forgot-password panel. Like the log-in panel it uses the redesigned column,
   * but its heading is left-aligned behind a back control rather than centred.
   */
  const isForgotPasswordPanel = (): boolean => {
    return type === AUTH_TYPE.FORGOT_PASSWORD;
  };

  /**
   * The student log-in panel. Shares the forgot-password panel's heading shape and
   * adds a teacher note below the form.
   */
  const isStudentLoginPanel = (): boolean => {
    return type === AUTH_TYPE.STUDENT_LOGIN;
  };

  const isConfirmLoginPanel = (): boolean => {
    return type === AUTH_TYPE.CONFIRM_LOGIN;
  };

  /**
   * The login page's own 2FA step. Every other surface keeps the modal.
   */
  const isTwoFactorVerificationPanel = (): boolean => {
    return type === AUTH_TYPE.TWO_FACTOR_VERIFICATION;
  };

  const hasPanelHeadingWithBackControl = (): boolean => {
    return isForgotPasswordPanel() || isStudentLoginPanel() || isConfirmLoginPanel() || isTwoFactorVerificationPanel() || isSignupPasswordPanel() || isSavedAccountPasswordPanel();
  };

  const shouldAdditionalActionsBeShown = (): boolean => {
    return isLoginOrSignupAuthType(type) && !showForm && !isStandardLoginPanel() && !isStandardSignupPanel();
  };

  /** Phones use a smaller back control: a 20px icon in a 40px box. */
  const getBackControlIconSize = (): IconSize => {
    return isPhoneLayout ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_24;
  };

  const getPanelHeadingWithBackControl = (): ReactElement => {
    return (
      <div className={styles.panelHeadingRow} data-testid={getHeadingTestId()}>
        <Icon
          ariaLabel={window.i18next.t('pmwjs_back')}
          className={styles.panelBackIcon}
          icon="icon-arrow-left"
          isUsedAsButton
          onClick={onBackButton}
          shape={IconShape.SQUARE}
          size={getBackControlIconSize()}
          type={IconType.TRANSPARENT}
        />
        <Text className={styles.panelTitle} val={getFormHeading()} />
      </div>
    );
  };

  const getSignupPanelHeading = (): ReactElement => {
    return (
      <div className={styles.loginPanelHeading} data-testid={getHeadingTestId()}>
        <Text val={getFormHeading()} className={styles.panelTitle} />
        <div className={styles.signupPrompt}>
          <Text val={window.i18next.t('pmwjs_user_login_prompt')} size={getAccountPromptTextSize()} />
          <Text
            val={window.i18next.t('pmwjs_log_in')}
            size={getAccountPromptTextSize()}
            bold
            className={styles.signupPromptLink}
            onClick={changeToLoginPage}
            attributes={{'data-testid': AuthTestId.FOOTER_LOGIN_PROMPT}}
          />
        </div>
      </div>
    );
  };

  /**
   * `InactiveUserPasswordResetForm` draws its own icon and title, so the panel contributes none — without
   * this the legacy shell renders an empty heading and its 32px margin above the form.
   */
  const getHeadingBlock = (): ReactElement | null => {
    if (isInactivePasswordResetAuthType(type) || isSavedAccountsPanel()) {
      return null;
    }
    if (hasPanelHeadingWithBackControl()) {
      return isModalSheetLayout ? null : getPanelHeadingWithBackControl();
    }
    if (isStandardSignupPanel()) {
      return getSignupPanelHeading();
    }
    return (
      <div className={styles.loginPanelHeading} data-testid={getHeadingTestId()}>
        <Text val={getFormHeading()} className={styles.panelTitle} />
        <div className={styles.signupPrompt}>
          <Text val={window.i18next.t('pmwjs_user_sign_up_prompt')} size={getAccountPromptTextSize()} />
          <Text
            val={window.i18next.t('pmwjs_sign_up_action')}
            size={getAccountPromptTextSize()}
            bold
            className={styles.signupPromptLink}
            onClick={changeToSignupPage}
            attributes={{'data-testid': AuthTestId.SIGN_UP_BUTTON}}
          />
        </div>
      </div>
    );
  };

  const getHeadingTestId = (): AuthTestId | undefined => {
    switch (type) {
      case AUTH_TYPE.SIGNUP:
        return AuthTestId.HEADING_SIGNUP;
      case AUTH_TYPE.STUDENT_LOGIN:
        return AuthTestId.HEADING_STUDENT_LOGIN;
      case AUTH_TYPE.FORGOT_PASSWORD:
        return AuthTestId.HEADING_FORGOT_PASSWORD;
      case AUTH_TYPE.LOGIN:
        return AuthTestId.HEADING_LOGIN;
      case AUTH_TYPE.SAVED_ACCOUNT_PASSWORD:
        return AuthTestId.HEADING_SAVED_ACCOUNT_PASSWORD;
      default:
        return undefined;
    }
  };

  const getContainerStyles = (): string => {
    if (isSavedAccountsPanel()) {
      return `${styles.container} ${styles.savedAccountsPanelLayout}`;
    }
    if (isSavedAccountPasswordPanel()) {
      return `${styles.container} ${styles.savedAccountPasswordPanelLayout}`;
    }
    if (isForgotPasswordPanel()) {
      return `${styles.container} ${styles.forgotPasswordPanelLayout}`;
    }
    if (isStudentLoginPanel()) {
      return `${styles.container} ${styles.studentLoginPanelLayout}`;
    }
    if (isConfirmLoginPanel()) {
      return `${styles.container} ${styles.confirmLoginPanelLayout}`;
    }
    if (isTwoFactorVerificationPanel()) {
      return `${styles.container} ${styles.twoFactorVerificationPanelLayout}`;
    }
    if (isSignupPasswordPanel()) {
      return `${styles.container} ${styles.signupPasswordPanelLayout}`;
    }
    if (isInactivePasswordResetAuthType(type)) {
      return `${styles.container} ${styles.inactivePasswordResetPanelLayout}`;
    }
    if (isStandardSignupPanel()) {
      return `${styles.container} ${styles.signupPanelLayout}`;
    }
    if (isStandardLoginPanel()) {
      return `${styles.container} ${styles.loginPanelLayout}`;
    }
    return styles.container;
  };

  const getSsoButtonStack = (): ReactElement | null => {
    if (isSavedAccountsPanel()) {
      return null;
    }
    return (
      <div className={getSsoButtonStackClassName()}>
        <GoogleLoginButton
          redirectUrl={getGoogleRedirectUrl()}
          isSignup={isSignupAuthType(type)}
          containerClassname={styles.ssoButtonSlot}
          size={getSsoButtonSize()}
          showProviderNameOnly={areSsoButtonsPaired()}
          onSignInDoneCallback={getGoogleSignInHandler()}
        />
        <AppleLogin
          redirectUrl={redirectUrl}
          isSignup={isSignupAuthType(type)}
          size={getSsoButtonSize()}
          showProviderNameOnly={areSsoButtonsPaired()}
          onAppleSignInDone={getAppleSignInHandler()}
        />
        <ButtonFacebookLogin onConnect={getFacebookConnectHandler()} isSignup={isSignupAuthType(type)} size={getSsoButtonSize()} showProviderNameOnly={areSsoButtonsPaired()} />
        {getStudentLoginButton()}
        {getLegacyOrDivider()}
      </div>
    );
  };

  const getSurfaceClassName = (): string => {
    if (surface === AuthSurface.PAGE) {
      return styles.surfacePage;
    }
    return isModalSheetLayout ? `${styles.surfaceModal} ${styles.surfaceModalSheet}` : styles.surfaceModal;
  };

  return (
    <div className={`${getContainerStyles()} ${getSurfaceClassName()}`}>
      {getHeadingBlock()}

      {getSsoButtonStack()}

      {getFormSection()}

      {shouldAdditionalActionsBeShown() ? (
          <>
            {isLoginAuthType(type) ? <LineDivider dividerClassName={styles.lineDivider} /> : null}
            {isSignupAuthType(type) ? <div className="spacing-m-t-2">
              <SignupAgreement isTextCenterAligned textSize={TextSize.MD} />
              <LineDivider dividerClassName={styles.lineDivider} />
            </div> : null}
            {getActionItems()}
            <LineDivider className="flex-row-justify-center" dividerClassName={`${styles.lineDivider} spacing-m-t-5`} />
            <SocialMediaIconsContainer />
          </>
      ) : null}
    </div>
  );
}
